'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'

export interface NavItemProps {
  href: string
  icon: React.ReactNode
  label: string
  badge?: string | number
  badgeType?: 'count' | 'live' | 'shortcut'
  collapsed?: boolean
}

export function NavItem({
  href,
  icon,
  label,
  badge,
  badgeType = 'count',
  collapsed = false,
}: NavItemProps) {
  const pathname = usePathname()
  const isActive = pathname === href || pathname.startsWith(href + '/')

  return (
    <Link
      href={href}
      className={`nav-item group relative flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
        collapsed ? 'justify-center' : ''
      } ${
        isActive
          ? 'bg-accent-primary/10 text-accent-primary'
          : 'text-text-secondary hover:bg-page hover:text-text-primary'
      }`}
    >
      {/* Active indicator */}
      {isActive && (
        <div
          className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 rounded-full"
          style={{ background: 'var(--accent-primary)' }}
        />
      )}

      {/* Icon */}
      <span className={`flex-shrink-0 ${isActive ? 'text-accent-primary' : ''}`}>
        {icon}
      </span>

      {/* Label - hidden when collapsed */}
      {!collapsed && (
        <span className="flex-1 text-sm font-medium truncate">{label}</span>
      )}

      {/* Badge */}
      {badge && !collapsed && (
        <span
          className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full ${
            badgeType === 'live'
              ? 'bg-positive/10 text-positive animate-pulse'
              : badgeType === 'shortcut'
              ? 'bg-page text-text-tertiary border border-border-subtle'
              : 'bg-accent-primary/10 text-accent-primary'
          }`}
        >
          {badge}
        </span>
      )}

      {/* Tooltip when collapsed */}
      {collapsed && (
        <div className="absolute left-full ml-2 px-2 py-1 rounded-md bg-text-primary text-white text-xs font-medium opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity whitespace-nowrap z-50">
          {label}
          {badge && (
            <span className="ml-1 opacity-70">({badge})</span>
          )}
        </div>
      )}
    </Link>
  )
}
